/**
 * Select-based search field component used within the search area.
 *
 * This component renders a selectable search field and forwards user
 * selections to the parent search state handler in a normalized format.
 *
 * It exists to encapsulate the UI and event translation for select-based
 * search inputs while leaving state ownership and query orchestration to
 * the surrounding search container.
 *
 * @module
 *
 * @author Gregor Schalm gregor.schalm@wum-studios.de
 */

import { IntlMessageValue, UniCol, UniForm } from "../../../../../@WUM/core/Elements";
import {searchCombination, SelectSearchField} from "./SearchFieldInterface";
import UniInputGroupText from "../../../../../@WUM/core/Elements/Form/Elements/InputGroupText";
import React, { ChangeEvent } from "react";
import UniInputGroup from "../../../../../@WUM/core/Elements/Form/Elements/InputGroup";

/**
 * Props for the SearchSelect component.
 *
 * @typedef {Object} SearchSelectProps
 * @property {any} Store - Shared store instance used for internationalization and contextual access.
 * @property {string} value - Current selected value of the field.
 * @property {(fieldName: string, value: string, combination: searchCombination) => void} onValueChange - Callback that propagates value changes to the parent component.
 */
interface SearchSelectProps extends SelectSearchField {
    Store: any;
    value: string;
    onValueChange: (fieldName: string, value: string, combination: searchCombination) => void;
}

/**
 * Renders a select field for structured search input.
 *
 * The component forwards each selection change together with the field
 * name and configured search combination to the parent handler.
 *
 * @param {SearchSelectProps} props - Component configuration and interaction handlers.
 * @returns {*} Rendered select search field.
 *
 */
export const SearchSelect = (props: SearchSelectProps) => {
    /**
     * Handles select value changes and forwards the new value to the parent component.
     *
     * @param {ChangeEvent<HTMLSelectElement>} event - Change event emitted by the select element.
     * @returns {void} Does not return a value.
     *
     */
    const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        props.onValueChange(props.name, event.target.value, props.combination ?? "and");
    };

    return (
        <>
            <UniCol>
                <UniInputGroup class="mb-3">
                    <UniInputGroupText
                        id={props.name}
                        text={IntlMessageValue("de", props.label, props.Store)}
                    />
                    <UniForm.Select
                        name={props.name}
                        items={props.options}
                        freeValueLabel={props.freeValueLabel}
                        freeValue={props.freeValue}
                        defaultValue={props.value}
                        onChange={handleChange}
                    />
                </UniInputGroup>
            </UniCol>
        </>
    );
};